import { useState, useEffect } from 'react';
import {
  onAuthStateChanged,
  signInWithCustomToken,
  signOut,
  User as FirebaseUser
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { requestOtp, verifyOtp } from '../services/otpAuth';
import { User, ProfileData } from '../types';
import toast from 'react-hot-toast';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsProfileCompletion, setNeedsProfileCompletion] = useState(false);
  const [lastUsedPhone, setLastUsedPhone] = useState<string>(() => {
    try { return localStorage.getItem('lastOtpPhone') || ''; } catch { return ''; }
  });

  useEffect(() => {
    let unsubscribeUserDoc: (() => void) | null = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      // Clean up previous user doc listener when auth changes
      if (unsubscribeUserDoc) {
        unsubscribeUserDoc();
        unsubscribeUserDoc = null;
      }

      if (firebaseUser) {
        setFirebaseUser(firebaseUser);

        const userRef = doc(db, 'users', firebaseUser.uid);
        const existing = await getDoc(userRef);
        if (!existing.exists()) {
          // Create minimal profile for brand new user
          const newUser: Partial<User> = {
            name: firebaseUser.displayName || '',
            photoURL: firebaseUser.photoURL || '',
            email: firebaseUser.email || '',
            phone: firebaseUser.phoneNumber || '',
            cashbackValue: 0,
            createdAt: new Date(),
            lastLoginAt: new Date(),
            lastSeen: new Date(),
            profileCompleted: false,
            authProvider: 'phone',
          };
          await setDoc(userRef, newUser);
        } else {
          // Update last seen for existing user
          try {
            await updateDoc(userRef, {
              lastSeen: new Date(),
              lastLoginAt: new Date()
            });
          } catch (error) {
            console.error('Error updating last seen:', error);
          }
        }

        // Live-listen for profile changes so router unlocks immediately
        unsubscribeUserDoc = onSnapshot(userRef, async (snap) => {
          if (snap.exists()) {
            const userData = snap.data() as any;

            // Backfill phone if missing in Firestore but available from auth
            const phoneFromAuth = firebaseUser.phoneNumber || lastUsedPhone || '';
            if ((!userData.phone || userData.phone === '') && phoneFromAuth) {
              try { await updateDoc(userRef, { phone: phoneFromAuth }); } catch { }
            }

            const userObj: User = {
              id: firebaseUser.uid,
              name: userData.name || '',
              photoURL: userData.photoURL || '',
              phone: userData.phone || '',
              upiId: userData.upiId || '',
              bankName: userData.bankName || '',
              accountNumber: userData.accountNumber || '',
              ifscCode: userData.ifscCode || '',
              accountHolderName: userData.accountHolderName || '',
              bankDocUrl: userData.bankDocUrl || '',
              upiDocUrl: userData.upiDocUrl || '',
              city: userData.city || '',
              state: userData.state || '',
              district: userData.district || '',
              pinCode: userData.pinCode || '',
              address: userData.address || '',
              accountType: userData.accountType || 'Painter',
              age: userData.age ?? undefined,
              email: firebaseUser.email || userData.email || '',
              couponUsed: userData.couponUsed,
              cashbackValue: userData.cashbackValue || 0,
              pointsBalance: userData.pointsBalance || 0,
              dateScanned: userData.dateScanned?.toDate(),
              lastScanDate: userData.lastScanDate?.toDate(),
              createdAt: userData.createdAt?.toDate() || new Date(),
              profileCompleted: userData.profileCompleted || false,
              authProvider: userData.authProvider || 'phone',
              // Security flags
              isBanned: userData.isBanned || false,
              bannedAt: userData.bannedAt?.toDate(),
              isFlagged: userData.isFlagged || false,
              flaggedAt: userData.flaggedAt?.toDate(),
              lastAction: userData.lastAction,
              lastActionDate: userData.lastActionDate?.toDate(),
              kyc: userData.kyc ? {
                realName: userData.kyc.realName || '',
                aadhaarNumber: userData.kyc.aadhaarNumber || '',
                aadhaarPhotoURL: userData.kyc.aadhaarPhotoURL || '',
                panNumber: userData.kyc.panNumber || '',
                panPhotoURL: userData.kyc.panPhotoURL || '',
                status: userData.kyc.status || 'not_submitted',
                submittedAt: userData.kyc.submittedAt?.toDate ? userData.kyc.submittedAt.toDate() : userData.kyc.submittedAt,
                reviewedAt: userData.kyc.reviewedAt?.toDate ? userData.kyc.reviewedAt.toDate() : userData.kyc.reviewedAt,
                rejectReason: userData.kyc.rejectReason || '',
                submitCount: userData.kyc.submitCount || 0,
                blockUntil: userData.kyc.blockUntil?.toDate ? userData.kyc.blockUntil.toDate() : userData.kyc.blockUntil,
                blockPermanent: userData.kyc.blockPermanent || false,
                needsBankResubmit: userData.kyc.needsBankResubmit || false,
              } : { status: 'not_submitted' },
              paymentChangeRequest: userData.paymentChangeRequest ? {
                status: userData.paymentChangeRequest.status || 'pending',
                type: userData.paymentChangeRequest.type || 'bank',
                bankName: userData.paymentChangeRequest.bankName || '',
                accountNumber: userData.paymentChangeRequest.accountNumber || '',
                ifscCode: userData.paymentChangeRequest.ifscCode || '',
                accountHolderName: userData.paymentChangeRequest.accountHolderName || '',
                bankDocUrl: userData.paymentChangeRequest.bankDocUrl || '',
                upiId: userData.paymentChangeRequest.upiId || '',
                upiDocUrl: userData.paymentChangeRequest.upiDocUrl || '',
                submittedAt: userData.paymentChangeRequest.submittedAt?.toDate ? userData.paymentChangeRequest.submittedAt.toDate() : userData.paymentChangeRequest.submittedAt,
                reviewedAt: userData.paymentChangeRequest.reviewedAt?.toDate ? userData.paymentChangeRequest.reviewedAt.toDate() : userData.paymentChangeRequest.reviewedAt,
                rejectReason: userData.paymentChangeRequest.rejectReason || '',
              } : undefined,
              // Dealer association
              dealerCode: userData.dealerCode || undefined,
              dealerId: userData.dealerId || undefined,
              dealerAssociatedAt: userData.dealerAssociatedAt?.toDate ? userData.dealerAssociatedAt.toDate() : userData.dealerAssociatedAt,
            };
            setUser(userObj);
            setNeedsProfileCompletion(!userObj.profileCompleted);
          }
          setLoading(false);
        }, () => {
          // On error, still end loading to avoid spinner lock
          setLoading(false);
        });
      } else {
        setUser(null);
        setFirebaseUser(null);
        setNeedsProfileCompletion(false);
        setLoading(false);
      }
    });

    return () => {
      if (unsubscribeUserDoc) unsubscribeUserDoc();
      unsubscribeAuth();
    };
  }, []);


  // OTP Flow (Fast2SMS via Cloud Functions)
  const startOtpLogin = async (phone: string) => {
    try {
      // Remember the phone used to request OTP for later backfill
      setLastUsedPhone(phone);
      try { localStorage.setItem('lastOtpPhone', phone); } catch { }

      const res = await requestOtp(phone);
      if (res.success) {
        toast.success('OTP sent successfully');
        return { success: true, message: res.message, expiresIn: res.expiresIn };
      } else {
        toast.error(res.message || 'Failed to send OTP');
        return { success: false, message: res.message };
      }
    } catch (error: any) {
      const message = error.message || 'Failed to send OTP';
      toast.error(message);
      return { success: false, message };
    }
  };

  const verifyOtpAndSignIn = async (phone: string, otp: string) => {
    try {
      const res = await verifyOtp(phone, otp);
      if (!res.success || !res.token) {
        toast.error(res.message || 'Invalid OTP');
        return false;
      }
      await signInWithCustomToken(auth, res.token);
      toast.success('Logged in successfully');
      return true;
    } catch (error: any) {
      const message = error.message || 'OTP verification failed';
      toast.error(message);
      return false;
    }
  };


  const completeProfile = async (profileData: ProfileData) => {
    if (!firebaseUser) return;

    try {
      // Log all data being uploaded
      console.log('🔵 [completeProfile] Starting profile update...');
      console.log('🔵 [completeProfile] User ID:', firebaseUser.uid);
      console.log('🔵 [completeProfile] Profile Data to upload:', {
        name: profileData.name,
        phone: profileData.phone,
        upiId: profileData.upiId,
        address: profileData.address || '',
        city: profileData.city,
        state: profileData.state,
        district: profileData.district,
        pinCode: profileData.pinCode,
        accountType: profileData.accountType || 'Painter',
        age: profileData.age || null,
        profileCompleted: true,
      });

      const updateData = {
        name: profileData.name,
        phone: profileData.phone,
        upiId: profileData.upiId,
        address: profileData.address || '',
        city: profileData.city,
        state: profileData.state,
        district: profileData.district,
        pinCode: profileData.pinCode,
        accountType: profileData.accountType || 'Painter',
        age: profileData.age || null,
        profileCompleted: true,
      };

      console.log('🔵 [completeProfile] Attempting to create/update Firestore document...');
      console.log('🔵 [completeProfile] Document path: users/' + firebaseUser.uid);
      console.log('🔵 [completeProfile] Authenticated UID:', firebaseUser.uid);
      console.log('🔵 [completeProfile] Auth UID matches document ID:', firebaseUser.uid === firebaseUser.uid);

      // Check if document exists first
      const userDocRef = doc(db, 'users', firebaseUser.uid);
      const existingDoc = await getDoc(userDocRef);
      console.log('🔵 [completeProfile] Document exists:', existingDoc.exists());

      // Use setDoc with merge: true to handle both create and update cases
      // This is necessary for first-time registration when document doesn't exist yet
      await setDoc(userDocRef, updateData, { merge: true });

      console.log('✅ [completeProfile] Firestore create/update successful!');

      setNeedsProfileCompletion(false);
      toast.success('Profile completed successfully!');

      // Refresh user data without page reload
      console.log('🔵 [completeProfile] Refreshing user data...');
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log('✅ [completeProfile] User data refreshed successfully');
        const updatedUser: User = {
          id: firebaseUser.uid,
          name: userData.name,
          photoURL: userData.photoURL,
          phone: userData.phone,
          upiId: userData.upiId,
          bankName: userData.bankName,
          accountNumber: userData.accountNumber,
          ifscCode: userData.ifscCode,
          accountHolderName: userData.accountHolderName,
          city: userData.city,
          state: userData.state,
          district: userData.district,
          pinCode: userData.pinCode,
          email: firebaseUser.email || '',
          couponUsed: userData.couponUsed,
          cashbackValue: userData.cashbackValue || 0,
          pointsBalance: userData.pointsBalance || 0,
          dateScanned: userData.dateScanned?.toDate(),
          lastScanDate: userData.lastScanDate?.toDate(),
          createdAt: userData.createdAt?.toDate() || new Date(),
          profileCompleted: userData.profileCompleted || false,
          authProvider: userData.authProvider || 'email',
          isBanned: userData.isBanned || false,
          bannedAt: userData.bannedAt?.toDate(),
          isFlagged: userData.isFlagged || false,
          flaggedAt: userData.flaggedAt?.toDate(),
          lastAction: userData.lastAction,
          lastActionDate: userData.lastActionDate?.toDate(),
          kyc: userData.kyc
            ? {
              realName: userData.kyc.realName || '',
              aadhaarNumber: userData.kyc.aadhaarNumber || '',
              aadhaarPhotoURL: userData.kyc.aadhaarPhotoURL || '',
              panNumber: userData.kyc.panNumber || '',
              panPhotoURL: userData.kyc.panPhotoURL || '',
              status: userData.kyc.status || 'not_submitted',
              submittedAt: userData.kyc.submittedAt?.toDate
                ? userData.kyc.submittedAt.toDate()
                : userData.kyc.submittedAt,
              reviewedAt: userData.kyc.reviewedAt?.toDate
                ? userData.kyc.reviewedAt.toDate()
                : userData.kyc.reviewedAt,
              rejectReason: userData.kyc.rejectReason || '',
              submitCount: userData.kyc.submitCount || 0,
              blockUntil: userData.kyc.blockUntil?.toDate
                ? userData.kyc.blockUntil.toDate()
                : userData.kyc.blockUntil,
              blockPermanent: userData.kyc.blockPermanent || false,
              needsBankResubmit: userData.kyc.needsBankResubmit || false,
            }
            : { status: 'not_submitted' },
          paymentChangeRequest: userData.paymentChangeRequest
            ? {
              status: userData.paymentChangeRequest.status || 'pending',
              type: userData.paymentChangeRequest.type || 'bank',
              bankName: userData.paymentChangeRequest.bankName || '',
              accountNumber: userData.paymentChangeRequest.accountNumber || '',
              ifscCode: userData.paymentChangeRequest.ifscCode || '',
              accountHolderName: userData.paymentChangeRequest.accountHolderName || '',
              bankDocUrl: userData.paymentChangeRequest.bankDocUrl || '',
              upiId: userData.paymentChangeRequest.upiId || '',
              upiDocUrl: userData.paymentChangeRequest.upiDocUrl || '',
              submittedAt: userData.paymentChangeRequest.submittedAt?.toDate
                ? userData.paymentChangeRequest.submittedAt.toDate()
                : userData.paymentChangeRequest.submittedAt,
              reviewedAt: userData.paymentChangeRequest.reviewedAt?.toDate
                ? userData.paymentChangeRequest.reviewedAt.toDate()
                : userData.paymentChangeRequest.reviewedAt,
              rejectReason: userData.paymentChangeRequest.rejectReason || '',
            }
            : undefined,
          dealerCode: userData.dealerCode || undefined,
          dealerId: userData.dealerId || undefined,
          dealerAssociatedAt: userData.dealerAssociatedAt?.toDate
            ? userData.dealerAssociatedAt.toDate()
            : userData.dealerAssociatedAt,
        };
        setUser(updatedUser);
      } else {
        console.warn('⚠️ [completeProfile] User document does not exist after update');
      }
    } catch (error: any) {
      console.error('❌ [completeProfile] Error occurred:', error);
      console.error('❌ [completeProfile] Error code:', error.code);
      console.error('❌ [completeProfile] Error message:', error.message);
      console.error('❌ [completeProfile] Full error object:', JSON.stringify(error, null, 2));

      // Log which specific field might be causing the issue
      if (error.code === 'permission-denied') {
        console.error('❌ [completeProfile] PERMISSION DENIED - Check Firestore rules for users collection');
        console.error('❌ [completeProfile] Attempted to update document: users/' + firebaseUser.uid);
        console.error('❌ [completeProfile] User authenticated:', !!firebaseUser);
        console.error('❌ [completeProfile] User UID:', firebaseUser?.uid);
      }

      toast.error(error.message || 'Missing or insufficient permission');
      throw error;
    }
  };

  // Optimistically mark profile as completed to unlock the app immediately
  const markProfileCompletedLocally = () => {
    setNeedsProfileCompletion(false);
    if (user) {
      setUser({ ...user, profileCompleted: true });
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      toast.success('Successfully logged out!');
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const refreshUserData = async () => {
    if (!firebaseUser) return;

    try {
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const updatedUser: User = {
          id: firebaseUser.uid,
          name: userData.name,
          photoURL: userData.photoURL,
          phone: userData.phone,
          upiId: userData.upiId,
          bankName: userData.bankName,
          accountNumber: userData.accountNumber,
          ifscCode: userData.ifscCode,
          accountHolderName: userData.accountHolderName,
          city: userData.city,
          state: userData.state,
          district: userData.district,
          pinCode: userData.pinCode,
          email: firebaseUser.email || '',
          couponUsed: userData.couponUsed,
          cashbackValue: userData.cashbackValue || 0,
          pointsBalance: userData.pointsBalance || 0,
          dateScanned: userData.dateScanned?.toDate(),
          lastScanDate: userData.lastScanDate?.toDate(),
          createdAt: userData.createdAt?.toDate() || new Date(),
          profileCompleted: userData.profileCompleted || false,
          authProvider: userData.authProvider || 'email',
          isBanned: userData.isBanned || false,
          bannedAt: userData.bannedAt?.toDate(),
          isFlagged: userData.isFlagged || false,
          flaggedAt: userData.flaggedAt?.toDate(),
          lastAction: userData.lastAction,
          lastActionDate: userData.lastActionDate?.toDate(),
          kyc: userData.kyc
            ? {
              realName: userData.kyc.realName || '',
              aadhaarNumber: userData.kyc.aadhaarNumber || '',
              aadhaarPhotoURL: userData.kyc.aadhaarPhotoURL || '',
              panNumber: userData.kyc.panNumber || '',
              panPhotoURL: userData.kyc.panPhotoURL || '',
              status: userData.kyc.status || 'not_submitted',
              submittedAt: userData.kyc.submittedAt?.toDate
                ? userData.kyc.submittedAt.toDate()
                : userData.kyc.submittedAt,
              reviewedAt: userData.kyc.reviewedAt?.toDate
                ? userData.kyc.reviewedAt.toDate()
                : userData.kyc.reviewedAt,
              rejectReason: userData.kyc.rejectReason || '',
              submitCount: userData.kyc.submitCount || 0,
              blockUntil: userData.kyc.blockUntil?.toDate
                ? userData.kyc.blockUntil.toDate()
                : userData.kyc.blockUntil,
              blockPermanent: userData.kyc.blockPermanent || false,
              needsBankResubmit: userData.kyc.needsBankResubmit || false,
            }
            : { status: 'not_submitted' },
          paymentChangeRequest: userData.paymentChangeRequest
            ? {
              status: userData.paymentChangeRequest.status || 'pending',
              type: userData.paymentChangeRequest.type || 'bank',
              bankName: userData.paymentChangeRequest.bankName || '',
              accountNumber: userData.paymentChangeRequest.accountNumber || '',
              ifscCode: userData.paymentChangeRequest.ifscCode || '',
              accountHolderName: userData.paymentChangeRequest.accountHolderName || '',
              bankDocUrl: userData.paymentChangeRequest.bankDocUrl || '',
              upiId: userData.paymentChangeRequest.upiId || '',
              upiDocUrl: userData.paymentChangeRequest.upiDocUrl || '',
              submittedAt: userData.paymentChangeRequest.submittedAt?.toDate
                ? userData.paymentChangeRequest.submittedAt.toDate()
                : userData.paymentChangeRequest.submittedAt,
              reviewedAt: userData.paymentChangeRequest.reviewedAt?.toDate
                ? userData.paymentChangeRequest.reviewedAt.toDate()
                : userData.paymentChangeRequest.reviewedAt,
              rejectReason: userData.paymentChangeRequest.rejectReason || '',
            }
            : undefined,
          dealerCode: userData.dealerCode || undefined,
          dealerId: userData.dealerId || undefined,
          dealerAssociatedAt: userData.dealerAssociatedAt?.toDate
            ? userData.dealerAssociatedAt.toDate()
            : userData.dealerAssociatedAt,
        };
        setUser(updatedUser);
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  const markKycStatusLocally = (partial: Partial<User['kyc']>) => {
    setUser(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        kyc: {
          ...(prev.kyc || { status: 'not_submitted' }),
          ...partial,
        },
      } as User;
    });
  };

  return {
    user,
    firebaseUser,
    loading,
    needsProfileCompletion,
    startOtpLogin,
    verifyOtpAndSignIn,
    completeProfile,
    markProfileCompletedLocally,
    logout,
    refreshUserData,
    markKycStatusLocally,
  };
}; 